import { useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { SavedScenario } from './useSavedScenarios';

interface ScenarioInputs {
  callsPerMonth: number;
  numberOfProperties: number;
  missedCallPercent: number;
  salesCallPercent: number;
  avgOrderValue: number;
  staffHoursWeek: number;
  hourlyStaffCost: number;
}

// Encode scenario inputs to URL-safe base64
const encodeScenario = (inputs: ScenarioInputs): string => {
  const data = [
    inputs.callsPerMonth,
    inputs.numberOfProperties,
    inputs.missedCallPercent,
    inputs.salesCallPercent,
    inputs.avgOrderValue,
    inputs.staffHoursWeek,
    inputs.hourlyStaffCost
  ].join(',');
  return btoa(data);
};

// Decode URL parameter back to scenario inputs
const decodeScenario = (encoded: string): ScenarioInputs | null => {
  try {
    const decoded = atob(encoded);
    const parts = decoded.split(',').map(Number);
    if (parts.length !== 7 || parts.some(isNaN)) return null;
    
    return {
      callsPerMonth: parts[0],
      numberOfProperties: parts[1],
      missedCallPercent: parts[2],
      salesCallPercent: parts[3],
      avgOrderValue: parts[4],
      staffHoursWeek: parts[5],
      hourlyStaffCost: parts[6]
    };
  } catch {
    return null;
  }
};

export const useScenarioSharing = (
  onLoadSharedScenario?: (inputs: ScenarioInputs) => void
) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Check for shared scenario on mount
  useEffect(() => {
    const sharedScenario = searchParams.get('scenario');
    if (sharedScenario && onLoadSharedScenario) {
      const decoded = decodeScenario(sharedScenario);
      if (decoded) {
        onLoadSharedScenario(decoded);
        // Clear the URL parameter after loading
        searchParams.delete('scenario');
        setSearchParams(searchParams, { replace: true });
      }
    }
  }, []);

  // Generate shareable URL for current scenario
  const generateShareUrl = useCallback((inputs: ScenarioInputs, language: string): string => {
    const encoded = encodeScenario(inputs);
    const baseUrl = `${window.location.origin}/${language}`;
    return `${baseUrl}?scenario=${encoded}#roi-calculator`;
  }, []);

  // Copy share URL to clipboard
  const copyShareUrl = useCallback(async (inputs: ScenarioInputs, language: string): Promise<boolean> => {
    const url = generateShareUrl(inputs, language);
    try {
      await navigator.clipboard.writeText(url);
      return true;
    } catch {
      return false;
    }
  }, [generateShareUrl]);

  // Generate share text with results
  const generateShareText = useCallback((
    inputs: ScenarioInputs,
    totalImpact: number,
    paybackMonths: number,
    language: string,
    formatCurrency: (v: number) => string
  ): string => {
    const url = generateShareUrl(inputs, language);
    const text = language === 'sv'
      ? `Jag beräknade mina besparingar med HostCall.ai: ${formatCurrency(totalImpact)}/år och återbetalningstid på ${paybackMonths.toFixed(1)} månader! 🚀\n\nPröva med dina siffror: ${url}`
      : `I calculated my savings with HostCall.ai: ${formatCurrency(totalImpact)}/year with a ${paybackMonths.toFixed(1)} month payback! 🚀\n\nTry with your numbers: ${url}`;
    return text;
  }, [generateShareUrl]);

  return {
    generateShareUrl,
    copyShareUrl,
    generateShareText
  };
};

export default useScenarioSharing;
